import { MAX_IN_FLIGHT_CHANNEL_MATCH_QUERIES } from "constants/streamConnection";
import { ChannelFilters, Event, StreamChat } from "stream-chat";
import { DefaultStreamChatGenerics } from "stream-chat-react/dist/types/types";

// Every mounted channel list listens to the same event, so identical checks
// for one event share a single queryChannels call instead of one per list.
const inFlightChannelMatchQueries = new Map<string, Promise<boolean>>();

const doesChannelMatchFilters = async <
  StreamChatGenerics extends DefaultStreamChatGenerics = DefaultStreamChatGenerics
>(
  client: StreamChat<StreamChatGenerics>,
  cid: string | undefined,
  filters: ChannelFilters<StreamChatGenerics>
): Promise<boolean> => {
  try {
    const [queriedChannel] = await client.queryChannels(
      {
        cid,
        ...filters,
      },
      [],
      { limit: 1, member_limit: 100 }
    );

    return queriedChannel?.cid === cid;
  } catch (error) {
    return false;
  }
};

export const shouldChannelListUpdate = async <
  StreamChatGenerics extends DefaultStreamChatGenerics = DefaultStreamChatGenerics
>({
  client,
  event,
  filters = {},
  isCustomer = false,
}: {
  client: StreamChat<StreamChatGenerics>;
  event?: Event<StreamChatGenerics>;
  filters?: ChannelFilters<StreamChatGenerics>;
  isCustomer?: boolean;
}): Promise<boolean> => {
  if (event?.message?.is_announcement && !isCustomer) {
    return false;
  }

  if (Object.keys(filters).length === 0) {
    return true;
  }

  const queryKey = `${event?.type}:${event?.cid}:${
    event?.message?.id
  }:${JSON.stringify(filters)}`;

  const inFlightQuery = inFlightChannelMatchQueries.get(queryKey);
  if (inFlightQuery) {
    return inFlightQuery;
  }

  if (inFlightChannelMatchQueries.size >= MAX_IN_FLIGHT_CHANNEL_MATCH_QUERIES) {
    inFlightChannelMatchQueries.clear();
  }

  const query = doesChannelMatchFilters(client, event?.cid, filters).finally(
    () => inFlightChannelMatchQueries.delete(queryKey)
  );
  inFlightChannelMatchQueries.set(queryKey, query);

  return query;
};
